import React, { useEffect, useState } from 'react';

/**
 * ThemeEnhancer - Wraps around existing Ghost template and adds contemporary styling
 * Does NOT replace any HTML - only enhances visual presentation
 */
const ThemeEnhancer = () => {
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(84);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);
    };

    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setHeaderHeight(64);
      } else if (window.innerWidth <= 768) {
        setHeaderHeight(72);
      } else if (window.innerWidth <= 1024) {
        setHeaderHeight(78);
      } else {
        setHeaderHeight(84);
      }
    };

    handleScroll();
    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Apply contemporary design enhancements to existing elements
  useEffect(() => {
    const header = document.querySelector('.gh-head');
    if (header) {
      if (scrolled) {
        header.style.background = 'rgba(15, 23, 42, 0.98)';
        header.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
        header.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
      } else {
        header.style.background = 'rgba(15, 23, 42, 0.96)';
        header.style.boxShadow = 'none';
        header.style.borderBottom = '1px solid rgba(255, 255, 255, 0.08)';
      }
    }
  }, [scrolled]);

  return (
    <>
      <style>{`
        /* Contemporary Art Theme Enhancement */
        /* Keep navy/black header - just enhance the styling */
        .gh-head {
          background: rgba(15, 23, 42, 0.96) !important;
          backdrop-filter: blur(20px) saturate(180%) !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
          transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }

        .gh-head::before {
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(255, 255, 255, 0.02) 100%) !important;
        }

        /* Keep white text on dark background */
        .gh-head-logo {
          color: #ffffff !important;
        }

        .gh-head-menu .nav a {
          color: rgba(255, 255, 255, 0.8) !important;
          background: transparent !important;
        }

        .gh-head-menu .nav a:hover {
          color: #ffffff !important;
          background: rgba(255, 255, 255, 0.12) !important;
        }

        /* Icon buttons for dark background */
        .gh-icon-btn {
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          color: rgba(255, 255, 255, 0.8) !important;
          background: transparent !important;
        }

        .gh-icon-btn:hover {
          background: rgba(255, 255, 255, 0.12) !important;
          border-color: rgba(255, 255, 255, 0.25) !important;
          color: #ffffff !important;
        }

        /* Language switcher button */
        .gh-language-switcher-btn {
          color: rgba(255, 255, 255, 0.8) !important;
        }

        .gh-language-switcher-dropdown {
          background: #222 !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2) !important;
        }

        .gh-language-switcher-dropdown button {
          color: #fff !important;
        }

        .gh-language-switcher-dropdown button:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }

        /* Member buttons */
        .gh-head-members .gh-head-link,
        .gh-head-members .gh-btn {
          color: rgba(255, 255, 255, 0.9) !important;
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
        }

        .gh-head-members .gh-head-link:hover,
        .gh-head-members .gh-btn:hover {
          background: rgba(255, 255, 255, 0.12) !important;
          border-color: rgba(255, 255, 255, 0.25) !important;
          color: #ffffff !important;
        }

        .gh-head-members .gh-primary-btn {
          background: var(--primary-color) !important;
          color: #ffffff !important;
          border-color: var(--primary-color) !important;
        }

        .gh-head-members .gh-primary-btn:hover {
          background: var(--primary-dark) !important;
          border-color: var(--primary-dark) !important;
        }

        /* Enhanced smooth transitions */
        .gh-head-menu .nav a,
        .gh-icon-btn,
        .gh-head-logo {
          transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }

        /* Mobile command bar - keep dark */
        .gh-command-bar {
          background: rgba(0, 0, 0, 0.85) !important;
          backdrop-filter: blur(20px) !important;
        }

        .gh-command-bar .nav a {
          color: rgba(255, 255, 255, 0.8) !important;
        }

        .gh-command-bar .nav a:hover {
          color: var(--primary-color) !important;
        }

        .gh-command-bar-close-btn {
          color: #fff !important;
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
        }

        .gh-command-bar-close-btn:hover {
          background: rgba(255, 255, 255, 0.12) !important;
        }

        .gh-command-bar-members .gh-head-link,
        .gh-command-bar-members .gh-btn {
          border: 2px solid #ffffff !important;
          color: #ffffff !important;
        }

        .gh-command-bar-members .gh-head-link:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }

        /* Enhanced card hover effects */
        .gh-card {
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }

        .gh-card:hover {
          transform: translateY(-6px) !important;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08) !important;
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth !important;
        }

        /* Enhanced focus states for accessibility */
        :where(a, button, [role="button"], input, select, textarea):focus-visible {
          outline: 3px solid var(--primary-color) !important;
          outline-offset: 3px !important;
          border-radius: 6px !important;
        }

        /* Footer stays dark - perfect contrast */
        .gh-foot {
          background: linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #334155 100%) !important;
        }

        /* Smooth animations for scroll-to-top */
        .gh-scroll-top {
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }

        .gh-scroll-top:hover {
          transform: translateY(-4px) scale(1.08) !important;
        }

        /* Enhanced pagination */
        .pagination-btn {
          transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
        }

        .pagination-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08) !important;
        }

        /* Subtle micro-interactions */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .gh-card {
          animation: fadeInUp 0.6s ease-out backwards;
        }

        .gh-card:nth-child(1) { animation-delay: 0.1s; }
        .gh-card:nth-child(2) { animation-delay: 0.2s; }
        .gh-card:nth-child(3) { animation-delay: 0.3s; }
        .gh-card:nth-child(4) { animation-delay: 0.4s; }
        .gh-card:nth-child(5) { animation-delay: 0.5s; }
        .gh-card:nth-child(6) { animation-delay: 0.6s; }

        /* Respect reduced motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .gh-head {
            backdrop-filter: blur(10px) !important;
          }
          
          .gh-command-bar {
            backdrop-filter: blur(10px) !important;
          }

          .gh-card {
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default ThemeEnhancer;
